import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { existsSync } from "node:fs";
import { dirname, join, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import { McpServer, ResourceTemplate } from "@modelcontextprotocol/sdk/server/mcp.js";
import { kernelQuery, getWorkspaceContext } from "../client.js";
import { trackCaptureContractMiss } from "../analytics.js";
import { AGENT_CHEATSHEET } from "./agentCheatsheet.js";

export { AGENT_CHEATSHEET } from "./agentCheatsheet.js";

/** MCP App View HTML bundles — served so Cursor/Claude can render tool UIs */
const UI_VIEWS: Record<string, string> = {
  "ui://entries/entry-cards.html": "src/entry-cards/index.html",
  "ui://graph/constellation.html": "src/graph-constellation/index.html",
};

export const UI_RESOURCE_MIME_TYPE = "text/html;profile=mcp-app";
const MODULE_DIR = dirname(fileURLToPath(import.meta.url));
const UI_VIEW_BASE_CANDIDATES = [
  resolve(MODULE_DIR, "views"),
  resolve(MODULE_DIR, "..", "views"),
  resolve(MODULE_DIR, "..", "..", "dist", "views"),
  resolve(MODULE_DIR, "..", "..", "..", "mcp-views", "dist"),
];

export function resolveUiViewPath(
  filePath: string,
  options?: {
    moduleDir?: string;
    pathExists?: (path: string) => boolean;
  },
): string | null {
  const moduleDir = options?.moduleDir ?? MODULE_DIR;
  const pathExists = options?.pathExists ?? existsSync;
  const candidateBases = options?.moduleDir
    ? [
        resolve(moduleDir, "views"),
        resolve(moduleDir, "..", "views"),
        resolve(moduleDir, "..", "..", "dist", "views"),
        resolve(moduleDir, "..", "..", "..", "mcp-views", "dist"),
      ]
    : UI_VIEW_BASE_CANDIDATES;

  for (const candidateBase of candidateBases) {
    const candidatePath = join(candidateBase, filePath);
    if (pathExists(candidatePath)) {
      return candidatePath;
    }
  }

  return null;
}

function renderMissingUiView(uri: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Product Brain View Unavailable</title>
  <style>
    body {
      margin: 0;
      padding: 20px;
      font-family: system-ui, sans-serif;
      background: #0f172a;
      color: #e2e8f0;
    }
    .panel {
      max-width: 720px;
      margin: 0 auto;
      padding: 20px;
      border: 1px solid #334155;
      border-radius: 12px;
      background: #111827;
    }
    h1 {
      margin: 0 0 12px;
      font-size: 18px;
    }
    p {
      margin: 0 0 10px;
      line-height: 1.5;
    }
    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      color: #93c5fd;
    }
  </style>
</head>
<body>
  <main class="panel">
    <h1>Product Brain view unavailable</h1>
    <p>The UI resource <code>${uri}</code> could not be loaded.</p>
    <p>If this is a local checkout, rebuild the MCP views bundle. If this is an installed package, the published package is missing its bundled views.</p>
  </main>
</body>
</html>`;
}

// ─── Response Types ──────────────────────────────────────────────────

interface EntryListItem {
  _id: string;
  entryId?: string;
  name: string;
  status: string;
  collectionName?: string;
  collectionSlug?: string;
  data?: Record<string, unknown>;
}

interface EntryRelation {
  type: string;
  direction: "outgoing" | "incoming";
  otherEntryId: string | null;
  otherName: string | null;
}

interface EntryDetail extends EntryListItem {
  relations?: EntryRelation[];
  labels?: Array<{ name: string; slug: string; color?: string }>;
  history?: Array<{ action: string; timestamp: number; actor?: string }>;
}

interface Collection {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  fields: Array<{ key: string; type: string; required?: boolean; searchable?: boolean }>;
}

interface LabelRecord {
  _id: string;
  name: string;
  slug: string;
  color?: string;
  isGroup?: boolean;
  parentId?: string;
}

interface CaptureContractField {
  key: string;
  required: boolean;
  type: string;
  helpText?: string;
}

interface CaptureContractResult {
  collectionSlug: string;
  requiredFields: string[];
  allFields: CaptureContractField[];
  captureExpectation?: string;
}

// `ContextResult` (a hand-written local mirror) retired — PR #395, Lane C S3b annotation
// pass. `result` below is typed via `GatewayRouteReturnByName<"chain.gatherContext">` (the
// real server-derived shape); the one remaining consumer (`byCollection` below) derives
// its Map value type from that route directly instead of a second, drifting mirror.
type GatherContextResult = GatewayRouteReturnByName<"chain.gatherContext">;

// ─── Helpers ─────────────────────────────────────────────────────────

function formatEntryMarkdown(entry: EntryListItem): string {
  const id = entry.entryId ? `${entry.entryId}: ` : "";
  const lines = [`## ${id}${entry.name} [${entry.status}]`];
  if (entry.data && typeof entry.data === "object") {
    for (const [key, val] of Object.entries(entry.data)) {
      if (val && key !== "rawData") {
        lines.push(`**${key}**: ${typeof val === "string" ? val : JSON.stringify(val)}`);
      }
    }
  }
  return lines.join("\n");
}

function buildOrientationMarkdown(
  collections: Collection[] | null,
  trackingEvents: EntryListItem[] | null,
  standards: EntryListItem[] | null,
  businessRules: EntryListItem[] | null,
  principles: EntryListItem[] | null,
): string {
  const sections: string[] = ["# Product Brain — Orientation"];

  // About Product Brain (the tool)
  sections.push(
    "## About Product Brain (PB)\n" +
    "PB is a knowledge management system used by many product teams. " +
    "This section describes how PB works — not your product.\n\n" +
    "### How PB Organizes Its Tools\n" +
    "- **Tools** = actions with side-effects or dynamic computation (capture, accept, search).\n" +
    "- **Resources** = stable, read-only data (orientation, terminology, collection schemas).\n" +
    "- **Prompts** = multi-step choreography (workflows, guided capture, deep dives).\n" +
    "- Every tool, resource, and prompt works for ANY workspace — no bespoke logic.\n" +
    "- Resource templates (URI params) serve workspace-specific data through generic patterns.\n" +
    "- Compound tools with `action` enums keep the tool count minimal.\n\n" +
    "### How PB Handles Your Data\n" +
    "- **Draft-first**: all writes create drafts. SSOT requires explicit user confirmation.\n" +
    "- **Empty-workspace safe**: every operation handles zero entries and fresh workspaces.\n" +
    "- **Advisory, not blocking**: quality scores, contradiction checks, and coaching never prevent operations.\n" +
    "- **Workspace-agnostic**: PB is a product for many teams — no workspace-specific logic.\n" +
    "- **Self-documenting**: orient and server instructions teach agents how PB works.",
  );

  // Workspace principles, standards, and rules from the Chain
  const wsRules: Array<{ id: string; name: string; severity?: string; source: string }> = [];

  for (const p of (principles ?? []).filter((e) => e.status === "active" || e.status === "verified")) {
    wsRules.push({ id: p.entryId ?? "", name: p.name, severity: (p.data?.severity as string) ?? undefined, source: "principles" });
  }
  for (const s of (standards ?? []).filter((e) => e.status === "active" || e.status === "verified")) {
    wsRules.push({ id: s.entryId ?? "", name: s.name, severity: (s.data?.severity as string) ?? undefined, source: "standards" });
  }
  for (const r of (businessRules ?? []).filter((e) => e.status === "active" || e.status === "verified")) {
    wsRules.push({ id: r.entryId ?? "", name: r.name, severity: (r.data?.severity as string) ?? undefined, source: "business-rules" });
  }

  if (wsRules.length > 0) {
    const ruleLines = wsRules
      .map((r) => {
        const sev = r.severity ? ` [${r.severity}]` : "";
        return `- **${r.id}**: ${r.name}${sev}`;
      })
      .join("\n");
    sections.push(
      `## Your Workspace Principles & Rules (${wsRules.length} active)\n` +
      "These are principles, standards, and rules your team has accepted onto the Chain. " +
      "Respect them during implementation.\n\n" +
      ruleLines + "\n\n" +
      'Use `entries action=get entryId="<ID>"` to drill into any rule before making changes in that area.',
    );
  } else {
    sections.push(
      "## Your Workspace Principles & Rules\n" +
      "No active principles, standards, or business rules on the Chain yet.\n" +
      "Use `capture` with collection `principles`, `standards`, or `business-rules` to add your team's guardrails.\n" +
      "Once accepted, they appear here at orient time — so every agent session starts with your rules visible.",
    );
  }

  // Core Product Architecture (the two primitives — L29 retired Map, WP-687)
  sections.push(
    "## Core Product Architecture\n" +
    "The Chain is a versioned, connected, compounding knowledge base — the SSOT for a product team.\n" +
    "Everything on the Chain is one of **two primitives**:\n\n" +
    "- **Entry** (atom) — a discrete knowledge unit: target audience, business rule, glossary term, metric, tension. Lives in a collection.\n" +
    "- **Process** (authored) — a narrative chain with named links (Strategy Coherence, IDM Proposal). Content is inline text. Collection: `chains`.\n\n" +
    "Entries are atoms. Processes author narrative from atoms.\n" +
    "Both share the same versioning, branching, gating, and relation system.\n\n" +
    "The Chain compounds: each new relation makes entries discoverable from more starting points → " +
    "context gathering returns richer results → AI processes have more context → quality scores improve → " +
    "next accept is smarter than the last.",
  );

  // Architecture
  sections.push(
    "## Architecture\n" +
    "```\n" +
    "Cursor (stdio) → MCP Server (mcp-server/src/index.ts)\n" +
    "  → POST /api/aki with Bearer token\n" +
    "  → Convex HTTP Action (convex/http.ts)\n" +
    "  → internalQuery / internalMutation (convex/agentKnowledge/*)\n" +
    "  → Convex DB (workspace-scoped)\n" +
    "```\n" +
    "Security: API key auth on every request, workspace-scoped data, internal functions blocked from external clients.\n" +
    "Key files: `packages/mcp-server/src/client.ts` (HTTP client + audit), `convex/schema.ts` (schema).",
  );

  // Data model
  if (collections) {
    const collList = collections
      .map((c) => {
        const prefix = c.icon ? `${c.icon} ` : "";
        return `- ${prefix}**${c.name}** (\`${c.slug}\`) — ${c.description || "no description"}`;
      })
      .join("\n");
    sections.push(
      `## Data Model (${collections.length} collections)\n` +
      "Unified entries model: collections define field schemas, entries hold data in a flexible `data` field.\n" +
      "The `data` field is polymorphic: plain fields for generic entries, `ChainData` (chainTypeId + links) for processes.\n" +
      "Tags for filtering (e.g. `severity:high`), relations via `entryRelations`, versions via `entryVersions`, labels via `labels` + `entryLabels`.\n\n" +
      collList + "\n\n" +
      "Use `collections action=list` for field schemas, `entries action=get` for full records.",
    );
  } else {
    sections.push(
      "## Data Model\n" +
      "Could not load collections — use `collections action=list` to browse manually.",
    );
  }

  // Business rules
  const rulesCount = businessRules ? `${businessRules.length} entries` : "not loaded — collection may not exist yet";
  sections.push(
    "## Business Rules\n" +
    `Collection: \`business-rules\` (${rulesCount}).\n` +
    "Find rules: `entries action=search` for text search, `entries action=list collection=business-rules` to browse.\n" +
    "Check compliance: use the `review-against-rules` prompt (pass a domain).\n" +
    "Draft a new rule: use the `draft-rule-from-context` prompt.",
  );

  // Analytics / tracking
  const eventsCount = trackingEvents ? `${trackingEvents.length} events` : "not loaded — collection may not exist yet";
  const conventionNote = standards
    ? "Naming convention: `object_action` in snake_case with past-tense verbs (from standards collection)."
    : "Naming convention: `object_action` in snake_case with past-tense verbs.";
  sections.push(
    "## Analytics & Tracking\n" +
    `Event catalog: \`tracking-events\` collection (${eventsCount}).\n` +
    `${conventionNote}\n` +
    "Implementation: `src/lib/analytics.ts`. Workspace-scoped events MUST use `withWorkspaceGroup()`.\n" +
    "Browse: `entries action=list collection=tracking-events`.",
  );

  // Knowledge Graph
  sections.push(
    "## Knowledge Graph\n" +
    "Entries are connected via typed relations (`entryRelations` table). Relations are bidirectional and collection-agnostic — any entry can link to any other entry.\n\n" +
    "**Recommended relation types** (extensible — any string accepted):\n" +
    "- `governs` — a rule constrains behavior of a feature\n" +
    "- `defines_term_for` — a glossary term is canonical vocabulary for a feature/area\n" +
    "- `belongs_to` — a feature belongs to a product area or parent concept (WP-641 typed successors: `anchored_to`, `authority_domain`)\n" +
    "- `informs` — a decision or insight informs a feature\n" +
    "- `surfaces_tension_in` — a tension exists within a feature area\n" +
    "- `related_to`, `depends_on`, `replaces`, `conflicts_with`, `references`, `confused_with`\n\n" +
    "Each relation type is defined as a glossary entry (prefix `GT-REL-*`) to prevent terminology drift. **Workflow-owned, NOT generic:** `domain_routed_to` (a question routed to a domain) — use the `question` tool (`action=create`/`adopt`, `domain=<slug>`), never `relations action=create`; the generic door has no replace/re-judge logic, so a raw write can leave duplicate routing edges and a stale suggested owner.\n\n" +
    "**Tools:**\n" +
    "- `context action=gather` — get the full context around any entry (multi-hop graph traversal)\n" +
    "- `relations action=suggest` — discover potential connections for an entry\n" +
    "- `relations action=create` — create a typed link between two entries\n" +
    "- `relations action=find` — list direct relations for an entry\n\n" +
    "**Convention:** When creating or updating entries in governed collections, always use `relations action=suggest` to discover and create relevant relations.",
  );

  // Creating Knowledge
  sections.push(
    "## Creating Knowledge\n" +
    "**Entries:** Use `capture` as the primary tool for creating new entries. It handles the full workflow in one call:\n" +
    "1. Creates the entry with collection-aware defaults (auto-fills dates, infers domains, sets priority)\n" +
    "2. Auto-links related entries from across the chain (up to 5 confident matches)\n" +
    "3. Returns a quality scorecard (X/10) with actionable improvement suggestions\n\n" +
    "**Smart profiles** exist for: `tensions`, `business-rules`, `glossary`, `decisions`, `features`, `audiences`, `strategy`, `standards`, `chains`, `tracking-events`.\n" +
    "All other collections use the `ENT-{random}` fallback profile.\n\n" +
    "**Processes:** Use `chain action=create` with a template (e.g., `strategy-coherence`, `idm-proposal`). Fill links with `chain action=edit`.\n\n" +
    "**Prompts** (invoke via MCP prompt protocol):\n" +
    "- `name-check` — verify a name against glossary conventions\n" +
    "- `draft-decision-record` — scaffold a decision entry\n" +
    "- `review-against-rules` — check work against business rules for a domain\n" +
    "- `draft-rule-from-context` — create a new business rule from context\n\n" +
    "Use `quality action=check` to score existing entries retroactively.\n" +
    "Use `entries action=update` for post-creation adjustments (status changes, field updates, deprecation).",
  );

  // Where to go next
  sections.push(
    "## Where to Go Next\n" +
    "- **Create entry** → `capture` tool (auto-links + quality score in one call)\n" +
    "- **Full context** → `context action=gather` (by entry ID or task description)\n" +
    "- **Discover links** → `relations action=suggest`\n" +
    "- **Quality audit** → `quality action=check`\n" +
    "- **Terminology** → `name-check` prompt or `productbrain://terminology` resource\n" +
    "- **Schema details** → `productbrain://collections` resource or `collections action=list`\n" +
    "- **Labels** → `productbrain://labels` resource or `collections action=label-list`\n" +
    "- **Any collection** → `productbrain://{slug}/entries` resource\n" +
    "- **Log a decision** → `draft-decision-record` prompt\n" +
    "- **Growth funnel** → `productbrain://growth-funnel` resource or `entries action=list collection=strategy status=draft`\n" +
    "- **Audiences** → `productbrain://audiences/entries` resource or `entries action=list collection=audiences`\n" +
    "- **Session identity** → `workspace action=whoami`\n" +
    "- **Health check** → `workspace action=check`\n" +
    "- **Debug MCP calls** → `workspace action=audit`",
  );

  return sections.join("\n\n---\n\n");
}

export function registerResources(server: McpServer) {
  // Agent cheatsheet: compact tool/schema reference (~500 tokens)
  server.resource(
    "agent-cheatsheet",
    "productbrain://agent-cheatsheet",
    async (uri) => ({
      contents: [{
        uri: uri.href,
        text: AGENT_CHEATSHEET,
        mimeType: "text/markdown",
      }],
    })
  );

  // Orientation: single-call system map for AI developers
  server.resource(
    "chain-orientation",
    "productbrain://orientation",
    async (uri) => {
      const [collectionsResult, eventsResult, standardsResult, rulesResult, principlesResult] = await Promise.allSettled([
        kernelQuery<Collection[]>("chain.listCollections"),
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "tracking-events" }),
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "standards" }),
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "business-rules" }),
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "principles" }),
      ]);

      const collections = collectionsResult.status === "fulfilled" ? collectionsResult.value : null;
      const trackingEvents = eventsResult.status === "fulfilled" ? eventsResult.value : null;
      const standards = standardsResult.status === "fulfilled" ? standardsResult.value : null;
      const businessRules = rulesResult.status === "fulfilled" ? rulesResult.value : null;
      const principles = principlesResult.status === "fulfilled" ? principlesResult.value : null;

      return {
        contents: [{
          uri: uri.href,
          // `chain.listEntries`'s real return is a union (raw EntryDoc[] vs Record<string,
          // unknown>[] depending on field projection, see wrapup.ts's own doc comment on this
          // recurring residual) — `EntryListItem` only reads a documented subset of fields
          // present on both union branches; cast rather than widen the render helper's
          // signature to the full server union (PR #395, Lane C S3b annotation pass).
          text: buildOrientationMarkdown(
            collections,
            trackingEvents as EntryListItem[] | null,
            standards as EntryListItem[] | null,
            businessRules as EntryListItem[] | null,
            principles as EntryListItem[] | null,
          ),
          mimeType: "text/markdown",
        }],
      };
    }
  );

  // Terminology: glossary + standards summary for deep-dives
  server.resource(
    "chain-terminology",
    "productbrain://terminology",
    async (uri) => {
      const [glossaryResult, standardsResult] = await Promise.allSettled([
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "glossary" }),
        kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: "standards" }),
      ]);

      const lines: string[] = ["# Product Brain — Terminology"];

      if (glossaryResult.status === "fulfilled") {
        const glossary = glossaryResult.value ?? [];
        if (glossary.length > 0) {
          const terms = glossary
            .map((t) => `- **${t.name}** (${t.entryId ?? "—"}) [${t.status}]: ${t.data?.canonical ?? t.data?.description ?? ""}`)
            .join("\n");
          lines.push(`## Glossary (${glossary.length} terms)\n\n${terms}`);
        } else {
          lines.push("## Glossary\n\nNo glossary terms yet. Use `capture` with collection `glossary` to add terms.");
        }
      } else {
        lines.push("## Glossary\n\nCould not load glossary — use `entries action=list collection=glossary` to browse manually.");
      }

      if (standardsResult.status === "fulfilled") {
        const standards = standardsResult.value ?? [];
        if (standards.length > 0) {
          const stds = standards
            .map((s) => `- **${s.name}** (${s.entryId ?? "—"}) [${s.status}]: ${s.data?.description ?? ""}`)
            .join("\n");
          lines.push(`## Standards (${standards.length} entries)\n\n${stds}`);
        } else {
          lines.push("## Standards\n\nNo standards yet. Use `capture` with collection `standards` to add standards.");
        }
      } else {
        lines.push("## Standards\n\nCould not load standards — use `entries action=list collection=standards` to browse manually.");
      }

      return {
        contents: [{ uri: uri.href, text: lines.join("\n\n---\n\n"), mimeType: "text/markdown" }],
      };
    }
  );

  server.resource(
    "chain-collections",
    "productbrain://collections",
    async (uri) => {
      const collections = (await kernelQuery<Collection[]>("chain.listCollections")) ?? [];

      if (collections.length === 0) {
        return { contents: [{ uri: uri.href, text: "No collections in this workspace. Ask a platform admin to provision one, or enable a module ontology pack — `collections action=create` is no longer self-serve.", mimeType: "text/markdown" }] };
      }

      const formatted = collections
        .map((c) => {
          const fieldList = (c.fields ?? [])
            .map((f) => `  - \`${f.key}\` (${f.type}${f.required ? ", required" : ""}${f.searchable ? ", searchable" : ""})`)
            .join("\n");
          return `## ${c.icon ?? ""} ${c.name} (\`${c.slug}\`)\n${c.description || ""}\n\n**Fields:**\n${fieldList}`;
        })
        .join("\n\n---\n\n");

      return {
        contents: [{ uri: uri.href, text: `# Knowledge Collections (${collections.length})\n\n${formatted}`, mimeType: "text/markdown" }],
      };
    }
  );

  server.resource(
    "chain-collection-entries",
    new ResourceTemplate("productbrain://{slug}/entries", {
      list: async () => {
        const collections = (await kernelQuery<Collection[]>("chain.listCollections")) ?? [];
        return {
          resources: collections.map((c) => ({
            uri: `productbrain://${c.slug}/entries`,
            name: `${c.icon ?? ""} ${c.name}`.trim(),
          })),
        };
      },
    }),
    async (uri, { slug }) => {
      // Same documented `chain.listEntries` union-vs-narrow-render-type cast as above.
      const entries = ((await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>("chain.listEntries", { collectionSlug: slug as string })) ?? []) as EntryListItem[];
      const formatted = entries.map(formatEntryMarkdown).join("\n\n---\n\n");

      return {
        contents: [{
          uri: uri.href,
          text: formatted || "No entries in this collection.",
          mimeType: "text/markdown",
        }],
      };
    }
  );

  server.resource(
    "chain-labels",
    "productbrain://labels",
    async (uri) => {
      const labels = (await kernelQuery<LabelRecord[]>("chain.listLabels")) ?? [];

      if (labels.length === 0) {
        return { contents: [{ uri: uri.href, text: "No labels in this workspace.", mimeType: "text/markdown" }] };
      }

      const groups = labels.filter((l) => l.isGroup);
      const ungrouped = labels.filter((l) => !l.isGroup && !l.parentId);
      const children = (parentId: string) => labels.filter((l) => l.parentId === parentId);

      const lines: string[] = [];
      for (const group of groups) {
        lines.push(`## ${group.name}`);
        for (const child of children(group._id)) {
          lines.push(`- \`${child.slug}\` ${child.name}${child.color ? ` (${child.color})` : ""}`);
        }
      }
      if (ungrouped.length > 0) {
        lines.push("## Ungrouped");
        for (const l of ungrouped) {
          lines.push(`- \`${l.slug}\` ${l.name}${l.color ? ` (${l.color})` : ""}`);
        }
      }

      return {
        contents: [{ uri: uri.href, text: `# Workspace Labels (${labels.length})\n\n${lines.join("\n")}`, mimeType: "text/markdown" }],
      };
    }
  );

  server.resource(
    "chain-entry",
    new ResourceTemplate("productbrain://entries/{entryId}", {
      list: undefined,
      complete: {
        entryId: async (value) => {
          if (!value || value.length < 1) return [];
          const entries = (await kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: value })) ?? [];
          return entries.slice(0, 10).map((e) => e.entryId ?? e._id);
        },
      },
    }),
    async (uri, { entryId }) => {
      const [entry, collections] = await Promise.all([
        kernelQuery<EntryDetail | null>("chain.getEntry", { entryId: entryId as string }),
        kernelQuery<Collection[]>("chain.listCollections"),
      ]);
      if (!entry) {
        return { contents: [{ uri: uri.href, text: `Entry "${entryId}" not found.`, mimeType: "text/markdown" }] };
      }

      const collectionMap = new Map((collections ?? []).map((c) => [c._id, c]));
      const col = collectionMap.get((entry as any).collectionId);
      const collLabel = col?.name ?? entry.collectionName ?? entry.collectionSlug ?? "unknown";

      const lines: string[] = [
        `# ${entry.entryId}: ${entry.name}`,
        `**Collection:** ${collLabel}`,
        `**Status:** ${entry.status}`,
        "",
      ];

      if (entry.data && typeof entry.data === "object") {
        lines.push("## Data");
        for (const [key, val] of Object.entries(entry.data)) {
          if (val && key !== "rawData") {
            const str = typeof val === "string" ? val : JSON.stringify(val, null, 2);
            lines.push(`**${key}:** ${str}`);
          }
        }
        lines.push("");
      }

      if (entry.relations && entry.relations.length > 0) {
        lines.push("## Relations");
        for (const rel of entry.relations) {
          const arrow = rel.direction === "outgoing" ? "→" : "←";
          const id = rel.otherEntryId ?? "";
          const name = rel.otherName ?? "(unknown)";
          lines.push(`- ${arrow} **${rel.type}** ${id}: ${name}`);
        }
        lines.push("");
      }

      if (entry.labels && entry.labels.length > 0) {
        lines.push(`## Labels\n${entry.labels.map((l) => `- ${l.name ?? l.slug}`).join("\n")}`);
      }

      return {
        contents: [{ uri: uri.href, text: lines.join("\n"), mimeType: "text/markdown" }],
      };
    }
  );

  server.resource(
    "chain-context",
    new ResourceTemplate("productbrain://context/{entryId}", {
      list: undefined,
      complete: {
        entryId: async (value) => {
          if (!value || value.length < 1) return [];
          const entries = (await kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: value })) ?? [];
          return entries.slice(0, 10).map((e) => e.entryId ?? e._id);
        },
      },
    }),
    async (uri, { entryId }) => {
      const result = await kernelQuery<GatewayRouteReturnByName<"chain.gatherContext">>("chain.gatherContext", {
        entryId: entryId as string,
        maxHops: 2,
      });

      if (!result?.root) {
        return { contents: [{ uri: uri.href, text: `Entry "${entryId}" not found.`, mimeType: "text/markdown" }] };
      }

      const lines: string[] = [
        `# Context: ${result.root.entryId}: ${result.root.name}`,
        `_${result.totalRelations} related entries (${result.hopsTraversed} hops)_`,
        "",
      ];

      const byCollection = new Map<string, NonNullable<GatherContextResult["related"]>>();
      for (const entry of result.related ?? []) {
        const key = entry.collectionName ?? "Uncategorized";
        if (!byCollection.has(key)) byCollection.set(key, []);
        byCollection.get(key)!.push(entry);
      }

      for (const [collName, entries] of byCollection) {
        lines.push(`## ${collName} (${entries.length})`);
        for (const e of entries) {
          const arrow = e.relationDirection === "outgoing" ? "→" : "←";
          const hopLabel = e.hop > 1 ? ` (hop ${e.hop})` : "";
          const id = e.entryId ? `${e.entryId}: ` : "";
          lines.push(`- ${arrow} **${e.relationType}** ${id}${e.name}${hopLabel}`);
        }
        lines.push("");
      }

      return {
        contents: [{ uri: uri.href, text: lines.join("\n"), mimeType: "text/markdown" }],
      };
    }
  );

  // WP-316 S1b: Capture contract resource — returns required fields and guidance for a collection.
  // Calls chain.getCaptureContract (registered in http.ts by S1a as internal.agentKnowledge.captureContracts.getCaptureContract).
  server.resource(
    "chain-collection-capture-contract",
    new ResourceTemplate("productbrain://collections/{slug}/capture-contract", {
      list: async () => {
        // Enumerate known collections so clients can auto-complete the slug
        const collections = (await kernelQuery<Collection[]>("chain.listCollections")) ?? [];
        return {
          resources: collections.map((c) => ({
            uri: `productbrain://collections/${c.slug}/capture-contract`,
            name: `${c.icon ?? ""} ${c.name} — capture contract`.trim(),
          })),
        };
      },
    }),
    async (uri, { slug }) => {
      const collectionSlug = slug as string;

      // Resolve workspaceId for PostHog miss event; non-fatal if resolution fails
      let workspaceId: string | null = null;
      try {
        const ctx = await getWorkspaceContext();
        workspaceId = ctx.workspaceId;
      } catch {
        // Workspace resolution failure must not block the resource response
      }

      let contract: Omit<CaptureContractResult, "collectionSlug"> | null = null;
      try {
        contract = await kernelQuery<GatewayRouteReturnByName<"chain.getCaptureContract">>(
          "chain.getCaptureContract",
          { collectionSlug },
        );
      } catch {
        // Query error — fall through to not-found response
        contract = null;
      }

      if (!contract) {
        // WP-316 E5: emit capture_contract_miss event for observability
        if (workspaceId) {
          trackCaptureContractMiss(workspaceId, { slug: collectionSlug });
        }

        return {
          contents: [{
            uri: uri.href,
            mimeType: "application/json",
            text: JSON.stringify({
              ok: false,
              error: `Collection '${collectionSlug}' not found in this workspace.`,
              hint: "Use `collections action=list` to see available collections and their slugs.",
            }),
          }],
        };
      }

      const result: CaptureContractResult = {
        collectionSlug,
        requiredFields: contract.requiredFields,
        allFields: contract.allFields,
        captureExpectation: contract.captureExpectation,
      };

      return {
        contents: [{
          uri: uri.href,
          mimeType: "application/json",
          text: JSON.stringify(result),
        }],
      };
    }
  );

  server.resource(
    "chain-search",
    new ResourceTemplate("productbrain://search/{query}", {
      list: undefined,
      complete: {
        query: async (value) => {
          if (!value) return ["glossary:", "business-rules:", "decisions:", "tensions:"];
          return [];
        },
      },
    }),
    async (uri, { query }) => {
      const results = await kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: query as string });

      if (!results || results.length === 0) {
        return { contents: [{ uri: uri.href, text: `No results for "${query}".`, mimeType: "text/markdown" }] };
      }

      const lines: string[] = [
        `# Search: "${query}"`,
        `_${results.length} results_`,
        "",
      ];

      for (const entry of results) {
        const id = entry.entryId ? `**${entry.entryId}:** ` : "";
        // `chain.searchEntries`'s real return (convex/agentKnowledge/entryReadQueries.ts's
        // searchEntriesReadModel: `EntryDoc & { collectionSlug: string | null }`) never had a
        // `collectionName` field — this fallback chain's first branch was always undefined at
        // runtime, silently falling through to `collectionSlug` (PR #395, Lane C S3b pass).
        const coll = entry.collectionSlug ?? "";
        lines.push(`- ${id}${entry.name} [${coll}] (${entry.status})`);
      }

      return {
        contents: [{ uri: uri.href, text: lines.join("\n"), mimeType: "text/markdown" }],
      };
    }
  );
}
